import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "react-oidc-context";
import { useAppSelector } from "@/store/hooks";
import {
  selectIsAdmin,
  selectIsReadOnlyUser,
  // selectAppRoles,
} from "@/store/selectors/authSelectors";

const UserProfileMenu: React.FC = () => {
  const navigate = useNavigate();
  const [isProfileClicked, setIsProfileClicked] = useState(false);
  const oidcAuth = useAuth();
  const { user } = oidcAuth;

  // Get user roles from Redux store
  const isAdmin = useAppSelector(selectIsAdmin);
  const isReadOnlyUser = useAppSelector(selectIsReadOnlyUser);
  // const appRoles = useAppSelector(selectAppRoles);

  // Create logger for this component

  const navigateToAdmin = () => {
    navigate("/admin");
  };

  const navigateToHome = () => {
    navigate("/");
  };

  const handleLogout = () => {
    oidcAuth.removeUser();
    oidcAuth.signoutRedirect();
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest("#userProfileMenu")) {
        setIsProfileClicked(false);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  const handleProfileClick = useCallback(() => {
    setIsProfileClicked(!isProfileClicked);
  }, [isProfileClicked]);

  // Determine if user should see admin menu
  const shouldShowAdmin = isAdmin || isReadOnlyUser;

  // Get admin menu text based on role
  const getAdminMenuText = isAdmin ? "Admin" : "Admin - Read Only";

  // Get admin menu icon based on role
  const getAdminMenuIcon = isAdmin
    ? "icon-admin_panel_settings"
    : "icon-visibility";

  const renderUserDropdowns = () => {
    return (
      <div
        // TODO: Enable for Adminicon
        // className={`hover:mb-2 text-blue font-medium rounded-b-md fixed top-[50px] object-right cursor-pointer select-none right-[20px] z-50 py-2 m-[10px] w-[250px] ${shouldShowAdmin ? "h-[150px]" : "h-[100px]"} bg-white shadow-lg border border-gray-200 focus:outline-none`}
        className={`hover:mb-2 text-blue-600 font-medium rounded-b-md fixed top-[50px] object-right cursor-pointer select-none right-[20px] z-50 py-2 m-[10px] w-[250px] h-[150px] bg-white ocus:outline-none `}
        role="menu"
        aria-orientation="vertical"
        aria-labelledby="user-menu-button"
      >
        {/* Search/Home Button */}
        <button
          className="flex m-4 hover:bg-blue-100/80 hover:font-bold w-[90%]"
          onClick={navigateToHome}
          type="button"
        >
          <div className="icon-search text-blue-600 text-3xl pr-2" />
          <span
            className="flex pl-[8px] text-lg text-center"
            role="menuitem"
            tabIndex={0}
            id="search"
          >
            Search
          </span>
        </button>

        {/* Conditionally render Admin button based on roles */}
        {shouldShowAdmin && (
          <button
            className="flex m-4 hover:bg-blue-100/80 hover:font-bold w-[90%]"
            onClick={navigateToAdmin}
            type="button"
            title={
              isReadOnlyUser && !isAdmin
                ? "Read-only access to admin features"
                : "Full admin access"
            }
          >
            <div
              className={`${getAdminMenuIcon} text-blue-600 text-3xl pr-2`}
            />
            <span
              className="inline-block pl-2 text-lg text-center"
              role="menuitem"
              tabIndex={0}
              id="admin"
            >
              {getAdminMenuText}
            </span>
          </button>
        )}

        {/* Logout Button */}
        <button
          type="button"
          onClick={handleLogout}
          className="hover:mb-2 xl:mb-2 mx-[16px] bg-blue py-[10px] h-[36px] w-[220px] font-normal text-[16px] text-[#fff] hover:bg-blue-600 text-center transition-colors duration-200 rounded"
          role="menuitem"
          tabIndex={0}
          id="logout"
        >
          Log out
        </button>
      </div>
    );
  };

  // Log role information when component mounts or roles change
  // useEffect(() => {
  //   if (appRoles.length > 0) {
  //     Logger.info("User profile menu initialized with roles", [
  //       "UserProfileMenu",
  //       JSON.stringify({
  //         userId: user?.profile?.sub,
  //         userName: user?.profile?.given_name || user?.profile?.name,
  //         appRoles,
  //         isAdmin,
  //         isReadOnlyUser,
  //         shouldShowAdmin,
  //       }),
  //     ]);
  //   }
  // }, [appRoles, isAdmin, isReadOnlyUser, shouldShowAdmin, user?.profile]);

  return (
    <div
      id="userProfileMenu"
      className="flex flex-row space-x-1 h-full font-medium content-center items-center justify-items-end justify-evenly px-[24px] cursor-pointer"
      onClick={handleProfileClick}
      role="button"
      tabIndex={0}
      aria-label="User profile menu"
      aria-expanded={isProfileClicked}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleProfileClick();
        }
      }}
    >
      <div className="flex-item font-robot align-middle text-lg w-full text-blue-600">
        Welcome, {user?.profile.given_name ?? user?.profile.name ?? "User"}!
      </div>
      <div
        className={`text-xl ${isProfileClicked ? "icon-keyboard_arrow_up" : "icon-keyboard_arrow_down"}`}
        aria-hidden="true"
      />
      <div
        className="icon-account_circle text-blue text-2xl"
        aria-hidden="true"
      />

      {isProfileClicked && renderUserDropdowns()}
    </div>
  );
};

export default UserProfileMenu;
